/**
 * eReg Protocols Management Page
 *
 * Create, edit, and manage protocols.
 */

import { useState } from 'react';
import {
  Plus,
  FileText,
  MoreVertical,
  Edit,
  Trash2,
  Users,
  Settings,
} from 'lucide-react';
import { useEreg } from '../contexts/EregContext';
import { useAuth } from '../../contexts/AuthContext';
import * as eregApi from '../lib/api';
import { PageHeader, SearchInput, fieldCls } from '../../ui';
import type { Protocol } from '../types';

export function Protocols() {
  const { protocols, protocolsLoading, refreshProtocols } = useEreg();
  const { currentSite, availableSites } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState<Protocol | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  // Filter protocols by search
  const filteredProtocols = protocols.filter((p) =>
    p.protocol_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.protocol_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.sponsor_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Create study handler — throws so the modal can surface 409 duplicates.
  const handleCreateProtocol = async (data: Partial<Protocol> & { allow_duplicate?: boolean }) => {
    await eregApi.createProtocol({
      ...data,
      site_id: data.site_id || currentSite?.id,
    });
    await refreshProtocols();
    setShowCreateModal(false);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title="Manage Studies"
        subtitle="Create and configure study document repositories"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
          >
            <Plus size={18} />
            <span>New Study</span>
          </button>
        }
      />

      {/* Search */}
      <SearchInput
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search studies..."
        label="Search studies"
      />

      {/* Protocols Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
        {protocolsLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-primary-400"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading studies...</p>
          </div>
        ) : filteredProtocols.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <FileText size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No studies found</p>
            {searchQuery && (
              <p className="text-sm mt-1">Try a different search term</p>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Study
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Sponsor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Site
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  PI
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
              {filteredProtocols.map((protocol) => (
                <tr key={protocol.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {protocol.protocol_number}
                      </div>
                      {protocol.protocol_title && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {protocol.protocol_title}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {protocol.sponsor_name || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {protocol.site?.name || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      protocol.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                        : protocol.status === 'startup'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                          : protocol.status === 'closed'
                            ? 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'
                    }`}>
                      {protocol.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {protocol.primary_investigator?.full_name || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm relative">
                    <button
                      onClick={() => setMenuOpen(menuOpen === protocol.id ? null : protocol.id)}
                      className="p-1 rounded transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <MoreVertical size={18} className="text-gray-400 dark:text-gray-500" />
                    </button>

                    {menuOpen === protocol.id && (
                      <div className="absolute right-6 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 dark:bg-gray-800 dark:border-gray-700">
                        <button
                          onClick={() => {
                            setSelectedProtocol(protocol);
                            setMenuOpen(null);
                          }}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          <Edit size={16} />
                          Edit Details
                        </button>
                        <button
                          onClick={() => setMenuOpen(null)}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          <Users size={16} />
                          Manage Access
                        </button>
                        <button
                          onClick={() => setMenuOpen(null)}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          <Settings size={16} />
                          Configure
                        </button>
                        <hr className="my-1 border-gray-200 dark:border-gray-700" />
                        <button
                          onClick={() => setMenuOpen(null)}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 transition-colors duration-150 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                        >
                          <Trash2 size={16} />
                          Archive
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal - simplified for now */}
      {showCreateModal && (
        <CreateProtocolModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateProtocol}
          sites={availableSites}
          currentSiteId={currentSite?.id}
        />
      )}
    </div>
  );
}

// Simple create protocol modal
function CreateProtocolModal({
  onClose,
  onSubmit,
  sites,
  currentSiteId,
}: {
  onClose: () => void;
  onSubmit: (data: Partial<Protocol> & { allow_duplicate?: boolean }) => Promise<void>;
  sites: Array<{ id: string; name: string }>;
  currentSiteId?: string;
}) {
  const [formData, setFormData] = useState({
    site_id: currentSiteId || '',
    protocol_number: '',
    protocol_title: '',
    sponsor_name: '',
    therapeutic_area: '',
    phase: '',
    irb_of_record: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const submitWith = async (allowDuplicate: boolean) => {
    if (!formData.protocol_number || !formData.site_id) return;
    setLoading(true);
    setError(null);
    try {
      await onSubmit(allowDuplicate ? { ...formData, allow_duplicate: true } : formData);
    } catch (err: any) {
      // Soft-uniqueness guard (§3.1): surface the duplicate + allow override.
      if (err?.response?.status === 409 && err.response.data?.code === 'EREG_DUPLICATE_PROTOCOL') {
        setIsDuplicate(true);
        setError('A study with this protocol number already exists at this site.');
      } else {
        setIsDuplicate(false);
        setError('Failed to create study. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitWith(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create New Study</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Site *
            </label>
            <select
              value={formData.site_id}
              onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
              className={`w-full ${fieldCls}`}
              required
            >
              <option value="">Select site...</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Protocol Number *
            </label>
            <input
              type="text"
              value={formData.protocol_number}
              onChange={(e) => setFormData({ ...formData, protocol_number: e.target.value })}
              placeholder="e.g., ABC-123"
              className={`w-full ${fieldCls}`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Study Title
            </label>
            <input
              type="text"
              value={formData.protocol_title}
              onChange={(e) => setFormData({ ...formData, protocol_title: e.target.value })}
              placeholder="Full study title"
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sponsor
              </label>
              <input
                type="text"
                value={formData.sponsor_name}
                onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phase
              </label>
              <select
                value={formData.phase}
                onChange={(e) => setFormData({ ...formData, phase: e.target.value })}
                className={`w-full ${fieldCls}`}
              >
                <option value="">Select...</option>
                <option value="I">Phase I</option>
                <option value="II">Phase II</option>
                <option value="III">Phase III</option>
                <option value="IV">Phase IV</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Therapeutic Area
            </label>
            <input
              type="text"
              value={formData.therapeutic_area}
              onChange={(e) => setFormData({ ...formData, therapeutic_area: e.target.value })}
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              IRB of Record
            </label>
            <input
              type="text"
              value={formData.irb_of_record}
              onChange={(e) => setFormData({ ...formData, irb_of_record: e.target.value })}
              className={`w-full ${fieldCls}`}
            />
          </div>

          {error && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              {error}
              {isDuplicate && (
                <button
                  type="button"
                  onClick={() => submitWith(true)}
                  disabled={loading}
                  className="ml-2 inline-flex min-h-[44px] items-center text-xs font-medium underline sm:min-h-0"
                >
                  Create anyway
                </button>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.protocol_number || !formData.site_id}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Study'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
